import React, { useState, useContext, useEffect } from 'react';
import AdSense from './AdSense';
import UpgradeModal from '../UpgradeModal/UpgradeModal';
import { AuthContext } from '../Providers/AuthContext';
import styles from './AdSenseLayout.module.css';

interface AdSenseLayoutProps {
  children: React.ReactNode;
  showAds?: boolean; // Allow conditional rendering
  hideUntilScroll?: boolean; // Hide ads until user scrolls past a threshold
}

/**
 * Layout wrapper that displays content with AdSense ads on the right side
 * Automatically hides ads for premium users
 */
function AdSenseLayout({ children, showAds = true, hideUntilScroll = false }: AdSenseLayoutProps) {
  const { isPremium, accountType, user } = useContext(AuthContext);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [hasScrolledPastThreshold, setHasScrolledPastThreshold] = useState(!hideUntilScroll);

  // Track scroll to show ads after parallax (roughly 2 viewport heights down)
  useEffect(() => {
    if (!hideUntilScroll) return;

    const handleScroll = () => {
      const scrollThreshold = window.innerHeight * 2;
      setHasScrolledPastThreshold(window.scrollY > scrollThreshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hideUntilScroll]);

  // Don't show ads if explicitly disabled, or if user is premium
  const shouldShowAds = showAds && !isPremium;

  // TEMP: Disable all ads + upsell UI during public testing.
  // Flip this to false (or delete it) when you're ready to re-enable ads.
  const adsDisabledForPublicTesting = true;

  // If ads are disabled (or user is premium), just render content as normal.
  if (!shouldShowAds || adsDisabledForPublicTesting) {
    return <>{children}</>;
  }

  return (
    <div className={styles.layoutContainer}>
      {/* Main content area */}
      <div className={styles.contentArea}>{children}</div>

      {/* Right sidebar with ads */}
      <div className={`${styles.adSidebar} ${!hasScrolledPastThreshold ? styles.adSidebarHidden : ''}`}>
        {/* AdSense Policy Fix: Use only ONE ad unit until approved */}
        <AdSense slotId="sidebar-main" />

        {/* Remove Ads button */}
        <button
          className={styles.removeAdsButton}
          onClick={() => setShowUpgradeModal(true)}
          title="Upgrade to Premium to remove ads"
        >
          Remove Ads
        </button>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        accountType={accountType}
        userEmail={user?.email}
        userId={user?.uid}
      />
    </div>
  );
}

export default AdSenseLayout;
